import Badge from '@/components/public/ui/Badge';
import Icon from '@/components/public/ui/Icon';
import LocationMap from '@/components/public/ui/LocationMap';
import SectionHeader from '@/components/public/ui/SectionHeader';
import { useSite } from '@/hooks/useSite';
import type { SiteLocation } from '@/types';
import { trackUmamiEvent } from '@/lib/analytics';

const directionsHref = (location: SiteLocation) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    location.address ?? location.name ?? '',
  )}`;

// Balcões de atendimento — map banner + one card per parish location + a phone
// bar. Canonical "service desks" block; reused on Services and Contact.
export default function ServiceDesks() {
  const site = useSite();
  const locations = site.locations;
  if (locations.length === 0) return null;

  const primary = locations.find((l) => l.headquarters) ?? locations[0];
  const mapPoint = locations.find((l) => l.latitude != null && l.longitude != null);

  return (
    <>
      <SectionHeader eyebrow="Atendimento" title="Os nossos balcões" />
      <div className="surface-card overflow-hidden">
        {mapPoint && (
          <div className="h-60 w-full">
            <LocationMap lat={mapPoint.latitude!} lng={mapPoint.longitude!} />
          </div>
        )}

        <div className="border-line grid grid-cols-1 border-t md:grid-cols-2">
          {locations.map((location, i) => (
            <div
              key={location.key}
              className={`flex flex-col p-6 sm:p-8 ${
                i % 2 === 0 && i < locations.length - 1
                  ? 'border-line border-b md:border-r md:border-b-0'
                  : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <h3 className="font-headline text-on-surface text-lg font-semibold">
                  {location.name}
                </h3>
                {location.headquarters && <Badge label="Sede" color="secondary" size="sm" />}
              </div>
              {location.address && (
                <p className="text-on-surface-light mt-1.5 text-sm">{location.address}</p>
              )}
              {location.openingHours && (
                <p className="text-on-surface-light mt-3 text-sm whitespace-pre-line">
                  {location.openingHours}
                </p>
              )}
              {location.address && (
                <a
                  href={directionsHref(location)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    trackUmamiEvent('directions_click', { location: location.name ?? '' })
                  }
                  aria-label={`Como chegar a ${location.name} — abre o Google Maps num novo separador`}
                  className="text-primary mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold"
                >
                  Como chegar
                  <Icon name="arrow_forward" size="sm" />
                </a>
              )}
            </div>
          ))}
        </div>

        {primary.phone && (
          <div className="section-ink flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-6 py-4 text-sm">
            <Icon name="phone" size="sm" />
            <span className="text-[#c2b9a6]">Atendimento telefónico:</span>
            <a
              href={`tel:${primary.phone.replace(/\s/g, '')}`}
              onClick={() => trackUmamiEvent('phone_click', { location: primary.name ?? '' })}
              className="font-semibold text-white"
            >
              {primary.phone}
            </a>
          </div>
        )}
      </div>
    </>
  );
}
